import { useEffect } from 'react';
import { writeText } from '../shell/dialogKit';

// New UI: field labels without the trailing colon ("Invoice Date" rather than "Invoice Date:"), as in Redwood and Fiori
// forms. Only the label's last text is touched; the fields, their names and their values are the editor's own.
export function stripLabelColon(label) {
	const walker = label.ownerDocument.createTreeWalker(label, NodeFilter.SHOW_TEXT);
	let last = null;
	for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.nodeValue.trim()) last = n;
	if (last && /:\s*$/.test(last.nodeValue)) {
		last.nodeValue = last.nodeValue.replace(/\s*:\s*$/, '');
		return true;
	}
	return false;
}

export function stripLabelColons(root, selector = '.row-form label') {
	if (!root) return 0;
	let n = 0;
	root.querySelectorAll(selector).forEach(l => { if (stripLabelColon(l)) n++; });
	return n;
}

// The same for classic forms on every page and in dialogs (the editor has its own EditorLabels). Dialogs are portals
// under <body>, so this watches the body.
// Classic sometimes labels a field with a Typography just before it ("Group Name:"), so those count as labels too.
const FIELD_NEXT = ':has(+ .MuiFormControl-root, + .MuiTextField-root, + .MuiInputBase-root, + .MuiAutocomplete-root, + select, + input)';
const PAGE_LABELS = [
	'.apw2-main label', '.MuiDialog-root:not(:has(.apw2-ed)) label', '.apw2-stamp-pop label',
	`.apw2-main .MuiTypography-root${FIELD_NEXT}`, `.MuiDialog-root:not(:has(.apw2-ed)) .MuiTypography-root${FIELD_NEXT}`
].join(', ');
// R14: the page's own title reads as its sidebar name ("User Groups Management" → "User groups"), a title drawn as an h4
// counts as the page heading, and the browser tab names the page ("Invoices · Media · APWorks"). A page the sidebar
// doesn't list only has its title's case changed to the crumb's (Link Integration's "Update Integration" stays).
export function syncPageTitle(doc = document) {
	const crumbs = [...doc.querySelectorAll('.apw2-crumbs > a, .apw2-crumbs > span:not(.sep)')].map(e => e.textContent.trim()).filter(Boolean);
	const page = crumbs[crumbs.length - 1];
	if (page) {
		const tab = [page, crumbs.length > 1 && crumbs[0] !== page ? crumbs[0] : null, 'APWorks'].filter(Boolean).join(' · ');
		if (doc.title !== tab) doc.title = tab;
	}
	const main = doc.querySelector('.apw2-main');
	if (!main || doc.querySelector('.apw2-body[data-shell-title]') || main.querySelector('.apw2-dash')) return false;
	const h = main.querySelector('h1') || main.querySelector('h4.MuiTypography-h4');
	if (!h) return false;
	if (h.tagName !== 'H1' && !h.getAttribute('role')) { h.setAttribute('role', 'heading'); h.setAttribute('aria-level', '1'); }
	const now = h.textContent.replace(/\s+/g, ' ').trim();
	// The sidebar's name when the page is in the sidebar; otherwise only the case of the crumb's name.
	const active = doc.querySelector('a.apw2-item.active');
	const name = active ? active.textContent.trim() : page && page.toLowerCase() === now.toLowerCase() ? page : '';
	if (!name || now === name) return false;
	writeText(h, name);
	return true;
}

export function PageLabels() {
	useEffect(() => {
		let frame = 0;
		const run = () => { frame = 0; stripLabelColons(document.body, PAGE_LABELS); syncPageTitle(document); };
		run();
		const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(run); });
		observer.observe(document.body, { childList: true, subtree: true, characterData: true });
		return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
	}, []);
	return null;
}

// Keeps the labels colon-free while the editor re-renders.
export function EditorLabels({ rootRef }) {
	useEffect(() => {
		const root = rootRef.current;
		if (!root) return undefined;
		let frame = 0;
		const run = () => { frame = 0; stripLabelColons(root); };
		run();
		const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(run); });
		observer.observe(root, { childList: true, subtree: true, characterData: true });
		return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
	}, [rootRef]);
	return null;
}
